import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, TrendingUp, Calendar, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('daily'); // daily or weekly

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  if (!data) return null;

  const chartData = view === 'daily' ? data.daily : data.weekly;
  const maxValue = Math.max(...chartData.map(d => d.unique_users), 1);

  const formatDate = (str) => {
    const d = new Date(str + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  const formatWeek = (str) => {
    const d = new Date(str + 'T00:00:00');
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    return `${d.getDate()}/${d.getMonth() + 1} - ${end.getDate()}/${end.getMonth() + 1}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto animate-fadeIn" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/admin')} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">Statistiche Accessi</h1>
          <p className="text-slate-500 text-sm">Monitoraggio attività utenti</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 text-center" data-testid="stat-today">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Users className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{data.today.unique_users}</p>
          <p className="text-xs text-slate-500">Oggi</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 text-center" data-testid="stat-week">
          <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <TrendingUp className="w-5 h-5 text-sky-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{data.this_week.unique_users}</p>
          <p className="text-xs text-slate-500">Questa Sett.</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 text-center" data-testid="stat-total">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{data.total_registered}</p>
          <p className="text-xs text-slate-500">Tot. Iscritti</p>
        </div>
      </div>

      {/* Chart Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('daily')}
          data-testid="view-daily-btn"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            view === 'daily' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Calendar className="w-4 h-4" /> Giornaliero
        </button>
        <button
          onClick={() => setView('weekly')}
          data-testid="view-weekly-btn"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            view === 'weekly' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <BarChart3 className="w-4 h-4" /> Settimanale
        </button>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 mb-6">
        <h3 className="font-outfit font-semibold text-slate-900 mb-4">
          {view === 'daily' ? 'Accessi Giornalieri (30 giorni)' : 'Accessi Settimanali (12 settimane)'}
        </h3>

        {chartData.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Nessun dato ancora disponibile</p>
        ) : (
          <div className="space-y-0">
            {/* Bar chart */}
            <div className="flex items-end gap-1 h-40 mb-2">
              {chartData.map((item, i) => {
                const height = Math.max(4, (item.unique_users / maxValue) * 100);
                const isToday = view === 'daily' && item.date === data.today.date;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    {/* Tooltip */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {item.unique_users} utenti
                    </div>
                    <div
                      className={`w-full rounded-t-md transition-all ${
                        isToday ? 'bg-emerald-500' : 'bg-sky-400 group-hover:bg-sky-500'
                      }`}
                      style={{ height: `${height}%`, minHeight: '4px' }}
                    />
                  </div>
                );
              })}
            </div>
            {/* Labels (show first, middle, last) */}
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>{view === 'daily' ? formatDate(chartData[0]?.date) : formatWeek(chartData[0]?.week)}</span>
              {chartData.length > 2 && (
                <span>{view === 'daily' ? formatDate(chartData[Math.floor(chartData.length / 2)]?.date) : formatWeek(chartData[Math.floor(chartData.length / 2)]?.week)}</span>
              )}
              <span>{view === 'daily' ? formatDate(chartData[chartData.length - 1]?.date) : formatWeek(chartData[chartData.length - 1]?.week)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900">
            {view === 'daily' ? 'Dettaglio Giornaliero' : 'Dettaglio Settimanale'}
          </h3>
        </div>
        <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
          {[...chartData].reverse().map((item, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
              <span className="text-sm text-slate-600">
                {view === 'daily' ? formatDate(item.date) : formatWeek(item.week)}
              </span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-400 rounded-full" style={{ width: `${(item.unique_users / maxValue) * 100}%` }} />
                </div>
                <span className="text-sm font-semibold text-slate-900 w-8 text-right">{item.unique_users}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
